import React, { useState, useEffect, useMemo } from "react";
import { ChevronDown, Upload, X } from "lucide-react";
import Modal from "../modal/Modal";
import { useEquipmentStore, usePremisesStore, useRegionStore } from "../../store/store";

interface ImportAssetsModalProps {
  open: boolean;
  onClose: () => void;
  equipmentCategory?: string;
  onImport: (formData: FormData) => Promise<void>;
}

interface ImportFormData {
  region: string;
  premise: string;
  equipmentCategory: string;
  assetSubcategory: string;
  file: File | null;
}

interface ImportFormErrors {
  region?: string;
  premise?: string;
  equipmentCategory?: string;
  assetSubcategory?: string;
  file?: string;
}

const ImportAssetsModal: React.FC<ImportAssetsModalProps> = ({
  open,
  onClose,
  equipmentCategory = "",
  onImport,
}) => {
  const [formData, setFormData] = useState<ImportFormData>({
    region: "",
    premise: "",
    equipmentCategory: equipmentCategory,
    assetSubcategory: "",
    file: null,
  });
  const [errors, setErrors] = useState<ImportFormErrors>({});
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const { equipmentData, assetSubcategoryData, allAssetsEquipment, allAssetSubcategories } = useEquipmentStore();
  const { allPremises, fetchPremises } = usePremisesStore();
  const { regions, fetchRegions } = useRegionStore();

  useEffect(() => {
    if (open) {
      if (!allPremises.length) fetchPremises();
      if (!regions.length) fetchRegions();
      if (!equipmentData.length) allAssetsEquipment();
      if (!assetSubcategoryData.length) allAssetSubcategories();
    }
  }, [open, fetchPremises, fetchRegions, allAssetsEquipment, allAssetSubcategories]);

  // useEffect(() => {
  //   if (allPremises.length > 0) {
  //   }
  //   if (regions.length > 0) {
  //   }
  // }, [allPremises, regions]);

  useEffect(() => {
    if (equipmentCategory) {
      setFormData(prev => ({ ...prev, equipmentCategory }));
    }
  }, [equipmentCategory]);

  const filteredPremises = useMemo(() => {
    if (!formData.region) return allPremises;
    
    const selectedRegion = regions.find((region: any) => region._id === formData.region);
    
    if (!selectedRegion) return allPremises;
    
    const filtered = allPremises.filter((premise: any) => {
      
      const match1 = premise.region === selectedRegion.regionName;
      const match2 = premise.region === selectedRegion._id;
      const match3 = premise.region === selectedRegion.regionName?.trim();
      
      return match1 || match2 || match3;
    });
    return filtered;
  }, [formData.region, allPremises, regions]);

  // useEffect(() => {
  // }, [filteredPremises]);

  const filteredSubcategories = formData.equipmentCategory
    ? assetSubcategoryData.filter((subcat: any) => subcat.categoryId === formData.equipmentCategory)
    : [];

  const handleInputChange = (field: keyof ImportFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field !== 'file' && errors[field as keyof ImportFormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    
    if (field === "region") {
      setFormData(prev => ({ ...prev, premise: "" }));
      setErrors(prev => ({ ...prev, premise: undefined }));
    }
    if (field === "equipmentCategory") {
      setFormData(prev => ({ ...prev, assetSubcategory: "" }));
      setErrors(prev => ({ ...prev, assetSubcategory: undefined }));
    }
  };

  const handleFileChange = (file: File | null) => {
    if (file) {
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        setErrors(prev => ({ ...prev, file: "File size must be less than 10MB" }));
        return;
      }

      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel', // .xls
        // 'text/csv' // .csv
      ];
      
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({ ...prev, file: "Please upload a valid Excel (.xlsx, .xls) or CSV file" }));
        return;
      }
    }

    setFormData(prev => ({ ...prev, file }));
    if (errors.file) {
      setErrors(prev => ({ ...prev, file: undefined }));
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ImportFormErrors = {};

    if (!formData.region || formData.region === "") {
      newErrors.region = "Please select a specific region";
    }
    if (!formData.premise || formData.premise === "") {
      newErrors.premise = "Please select a specific premise";
    }
    if (!formData.equipmentCategory || formData.equipmentCategory === "") {
      newErrors.equipmentCategory = "Please select an equipment category";
    }
    if (!formData.assetSubcategory || formData.assetSubcategory === "") {
      newErrors.assetSubcategory = "Please select an asset subcategory";
    }
    if (!formData.file) {
      newErrors.file = "Please upload a file";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsUploading(true);
    try {
      const submitFormData = new FormData();
      submitFormData.append('region', formData.region);
      submitFormData.append('premise', formData.premise);
      submitFormData.append('equipmentCategory', formData.equipmentCategory);
      submitFormData.append('assetSubcategory', formData.assetSubcategory);
      if (formData.file) {
        submitFormData.append('file', formData.file);
      }

      await onImport(submitFormData);
      handleClose();
    } catch (error) {
      console.error('Import failed:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      region: "",
      premise: "",
      equipmentCategory: equipmentCategory,
      assetSubcategory: "",
      file: null,
    });
    setErrors({});
    setIsUploading(false);
    onClose();
  };

  if (!open) return null;

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      closeButton={false}
      modalClass="bg-white w-full max-w-2xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
    >
      <div className="pt-6 px-6 flex-1 overflow-y-auto">
        <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
          Import Assets
        </h2>
        <form onSubmit={handleSubmit} id="importAssetsForm" className="flex flex-col gap-6 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Region <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.region}
                onChange={(e) => handleInputChange("region", e.target.value)}
                className={`w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  errors.region ? "border-red-500" : ""
                }`}
                // required
              >
                <option value="">Select Region</option>
                {regions.map((region: any) => (
                  <option key={region._id} value={region._id}>
                    {region.regionName}
                  </option>
                ))}
              </select>
              {errors.region && (
                <p className="mt-1 text-sm text-red-500">{errors.region}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Premise <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.premise}
                onChange={(e) => handleInputChange("premise", e.target.value)}
                disabled={!formData.region}
                className={`w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  errors.premise ? "border-red-500" : ""
                }`}
                // required
              >
                <option value="">
                  {formData.region ? "Select Premise" : "Select Region first"}
                </option>
                {filteredPremises.map((premise: any) => (
                  <option key={premise._id} value={premise._id}>
                    {premise.premisesName}
                  </option>
                ))}
              </select>
              {errors.premise && (
                <p className="mt-1 text-sm text-red-500">{errors.premise}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Asset Category <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.equipmentCategory}
                onChange={(e) => handleInputChange("equipmentCategory", e.target.value)}
                className={`w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none ${
                  errors.equipmentCategory ? "border-red-500" : ""
                }`}
                // required
              >
                <option value="">Select Asset Category</option>
                {equipmentData.map((equipment: any) => (
                  <option key={equipment._id} value={equipment._id}>
                    {equipment.equipmentName}
                  </option>
                ))}
              </select>
              {errors.equipmentCategory && (
                <p className="mt-1 text-sm text-red-500">{errors.equipmentCategory}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Asset Subcategory <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.assetSubcategory}
                onChange={(e) => handleInputChange("assetSubcategory", e.target.value)}
                disabled={!formData.equipmentCategory}
                className={`w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  errors.assetSubcategory ? "border-red-500" : ""
                }`}
                // required
              >
                <option value="">
                  {formData.equipmentCategory ? "Select Asset Subcategory" : "Select Asset Category first"}
                </option>
                {filteredSubcategories.map((subcategory: any) => (
                  <option key={subcategory._id} value={subcategory._id}>
                    {subcategory.subcategoryName}
                  </option>
                ))}
              </select>
              {errors.assetSubcategory && (
                <p className="mt-1 text-sm text-red-500">{errors.assetSubcategory}</p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Upload File <span className="text-red-500">*</span>
            </label>
            <div
              className={`relative border-2 border-dashed rounded-md p-4 transition-colors ${
                dragActive
                  ? "border-blue-500 bg-blue-50"
                  : errors.file
                  ? "border-red-500 bg-red-50"
                  : "border-gray-300 bg-gray-50"
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {formData.file ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Upload className="w-6 h-6 text-green-500" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {formData.file instanceof File ? formData.file.name : 'Unknown file'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formData.file instanceof File ? (formData.file.size / (1024 * 1024)).toFixed(2) : '0'} MB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleFileChange(null)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-1">
                    Drag and drop your file here, or{" "}
                    <button
                      type="button"
                      className="text-blue-500 hover:text-blue-700 font-medium"
                      onClick={() => document.getElementById("file-input")?.click()}
                    >
                      browse
                    </button>
                  </p>
                  <p className="text-xs text-gray-500">
                    Excel (.xlsx, .xls) files up to 10MB
                  </p>
                </div>
              )}
              <input
                id="file-input"
                type="file"
                name="file"
                accept=".xlsx,.xls"
                onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                className="hidden"
              />
            </div>
            {errors.file && (
              <p className="mt-1 text-sm text-red-500">{errors.file}</p>
            )}
          </div>

          <div className="h-20" />
        </form>
      </div>

      <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
        <button
          type="button"
          className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-gray-600 transition"
          onClick={handleClose}
          disabled={isUploading}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="importAssetsForm"
          className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition flex items-center space-x-2"
          disabled={isUploading}
        >
          {isUploading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Uploading...</span>
            </>
          ) : (
            <>
              <Upload className="w-4 h-4" />
              <span>Upload</span>
            </>
          )}
        </button>
      </div>
    </Modal>
  );
};

export default ImportAssetsModal;
